import { pb } from "./pocketbase";
import { getBaseSku, cleanSku } from "../utils/skuUtils";

/**
 * getArquivosArray — Normaliza o retorno de campos de arquivos do PocketBase.
 * Suporta Array nativo, string JSON ou string separada por vírgulas.
 */
export const getArquivosArray = (arquivos) => {
  if (!arquivos) return [];
  if (Array.isArray(arquivos)) return arquivos;
  if (typeof arquivos === "string") {
    try {
      const parsed = JSON.parse(arquivos);
      if (Array.isArray(parsed)) return parsed;
    } catch (e) {
      if (arquivos.includes(",")) {
        return arquivos.split(",").map(s => s.trim()).filter(Boolean);
      }
    }
    const trimmed = arquivos.trim();
    return trimmed ? [trimmed] : [];
  }
  return [];
};

/**
 * produtoDadosService — Serviço centralizado e resiliente para a coleção produto_dados
 * 
 * Garante idempotência por sku_pai, resolve race conditions e duplicatas antigas,
 * e gerencia uploads/deleções de arquivos de modelagem e imagens no PocketBase.
 */
export const produtoDadosService = {
  /**
   * Busca o registro canônico de produto_dados para o SKU Pai.
   * Se houver duplicatas no banco, seleciona com prioridade o registro que contenha
   * arquivos anexados (imagens/modelagens), ficha técnica ou mais recentemente atualizado.
   */
  async getProdutoDados(skuPai, options = {}) {
    if (!skuPai) return null;
    const cleanPai = getBaseSku(cleanSku(skuPai));
    if (!cleanPai) return null;

    try {
      const records = await pb.collection("produto_dados").getFullList({
        filter: `sku_pai = "${cleanPai}"`,
        sort: "-updated",
        expand: options.expand || "categoria,tipo_joia"
      });

      if (!records || records.length === 0) return null;
      if (records.length === 1) return records[0];

      // Se há duplicatas, escolhe o registro mais rico (com arquivos ou ficha técnica)
      const scoredRecords = records.map(rec => {
        let score = 0;
        const imgArr = getArquivosArray(rec.arquivos_imagens);
        const modArr = getArquivosArray(rec.arquivos_modelagem);

        if (imgArr.length > 0) score += 100 * imgArr.length;
        if (modArr.length > 0) score += 100 * modArr.length;
        if (rec.ficha_tecnica && Object.keys(rec.ficha_tecnica).length > 0) score += 50;
        if (rec.categoria) score += 20;
        if (rec.tipo_joia) score += 20;
        if (rec.custo_bom_centavos) score += 10;
        if (rec.links_legado && Object.keys(rec.links_legado).length > 0) score += 10;
        if (rec.observacoes_internas) score += 5;

        return { record: rec, score };
      });

      scoredRecords.sort((a, b) => b.score - a.score);
      const chosenRecord = scoredRecords[0].record;

      console.info(`[produtoDadosService] SKU "${cleanPai}" possui ${records.length} registros duplicados. Registro selecionado: ${chosenRecord.id} (Score: ${scoredRecords[0].score})`);

      return chosenRecord;
    } catch (err) {
      console.warn(`[produtoDadosService] Erro ao buscar produto_dados para SKU "${cleanPai}":`, err);
      return null;
    }
  },

  /**
   * Upsert atômico e idempotente para produto_dados.
   * Garante que NUNCA será criado um registro duplicado para o mesmo sku_pai.
   * Preserva metadados estendidos dentro do JSON ficha_tecnica caso colunas raiz não existam no PB.
   */
  async upsertProdutoDados(skuPai, payload, options = {}) {
    if (!skuPai) throw new Error("skuPai é obrigatório para upsertProdutoDados.");
    const cleanPai = getBaseSku(cleanSku(skuPai));

    const existing = await this.getProdutoDados(cleanPai, options);

    let finalPayload = payload;
    if (!(payload instanceof FormData) && payload && typeof payload === "object") {
      const currentFicha = (existing?.ficha_tecnica && typeof existing.ficha_tecnica === "object")
        ? existing.ficha_tecnica
        : {};
      const incomingFicha = (payload.ficha_tecnica && typeof payload.ficha_tecnica === "object")
        ? payload.ficha_tecnica
        : null;

      const extraFichaFields = {};
      if (payload.preco_venda_manual !== undefined) extraFichaFields.preco_venda_manual = payload.preco_venda_manual;
      if (payload.preco_venda_centavos !== undefined) extraFichaFields.preco_venda_centavos = payload.preco_venda_centavos;
      if (payload.custo_editado_manualmente !== undefined) extraFichaFields.custo_editado_manualmente = payload.custo_editado_manualmente;
      if (payload.custo_bom_centavos !== undefined) extraFichaFields.custo_bom_centavos = payload.custo_bom_centavos;
      if (payload.tipo_joia_nome !== undefined) extraFichaFields.tipo_joia_nome = payload.tipo_joia_nome;
      if (payload.categoria_nome !== undefined) extraFichaFields.categoria_nome = payload.categoria_nome;

      if (incomingFicha || Object.keys(extraFichaFields).length > 0) {
        finalPayload = {
          ...payload,
          ficha_tecnica: {
            ...currentFicha,
            ...(incomingFicha || {}),
            ...extraFichaFields
          }
        };
      }
    }

    if (existing) {
      // Atualiza registro existente
      return await pb.collection("produto_dados").update(existing.id, finalPayload, options);
    } else {
      // Cria novo registro único
      if (finalPayload instanceof FormData) {
        if (!finalPayload.has("sku_pai")) finalPayload.append("sku_pai", cleanPai);
        if (!finalPayload.has("estado_modelagem")) finalPayload.append("estado_modelagem", "sem_modelo");
        return await pb.collection("produto_dados").create(finalPayload, options);
      } else {
        const createData = {
          sku_pai: cleanPai,
          estado_modelagem: "sem_modelo",
          ...finalPayload
        };
        return await pb.collection("produto_dados").create(createData, options);
      }
    }
  },

  /**
   * Atualiza Custo (BOM ou Manual) e Preço de Venda de forma sincronizada entre produto_dados e catalog.
   */
  async updatePrecoECusto(skuPai, { custoCentavos, precoVendaBRL, isManualCost = true } = {}) {
    if (!skuPai) return null;
    const cleanPai = getBaseSku(cleanSku(skuPai));
    const now = new Date().toISOString();

    const payload = {
      custo_calculado_em: now,
      custo_editado_manualmente: !!isManualCost
    };

    if (typeof custoCentavos === "number" && !isNaN(custoCentavos) && custoCentavos >= 0) {
      payload.custo_bom_centavos = Math.round(custoCentavos);
    }

    const hasValidPrice = typeof precoVendaBRL === "number" && !isNaN(precoVendaBRL) && precoVendaBRL > 0;
    if (hasValidPrice) {
      payload.preco_venda_manual = precoVendaBRL;
      payload.preco_venda_centavos = Math.round(precoVendaBRL * 100);
    }

    const updatedRecord = await this.upsertProdutoDados(cleanPai, payload, {
      expand: "categoria,tipo_joia"
    });

    // Sincroniza o preço de venda na collection catalog (raiz + full_data.price) para o SKU Pai e variações
    if (hasValidPrice) {
      try {
        const catalogRecs = await pb.collection("catalog").getFullList({
          filter: `sku = "${cleanPai}" || sku ~ "${cleanPai}-"`
        });

        for (const catRec of catalogRecs) {
          const recPai = getBaseSku(cleanSku(catRec.sku || catRec.full_data?.sku));
          if (recPai !== cleanPai) continue;

          const updatedFullData = {
            ...(catRec.full_data || {}),
            price: precoVendaBRL
          };

          await pb.collection("catalog").update(catRec.id, {
            price: precoVendaBRL,
            full_data: updatedFullData
          }).catch(async () => {
            await pb.collection("catalog").update(catRec.id, {
              full_data: updatedFullData
            }).catch(() => { });
          });
        }
      } catch (catErr) {
        console.warn("[produtoDadosService] Aviso ao sincronizar preço no catalog:", catErr);
      }
    }

    // Dispara evento em tempo real para atualizar CatalogContext e componentes abertos
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("produto-dados-preco-updated", {
        detail: {
          skuPai: cleanPai,
          precoVendaBRL: hasValidPrice ? precoVendaBRL : undefined,
          custoCentavos: payload.custo_bom_centavos,
          isManualCost: !!isManualCost,
          record: updatedRecord
        }
      }));
    }

    return updatedRecord;
  },

  /**
   * Anexa um ou múltiplos arquivos de imagem a produto_dados.
   * Utiliza 'arquivos_imagens+' no FormData para manter arquivos existentes no PocketBase.
   */
  async uploadImages(skuPai, files) {
    if (!skuPai || !files || files.length === 0) return null;
    const cleanPai = getBaseSku(cleanSku(skuPai));

    // Garante que o registro base existe antes de enviar os arquivos
    const currentRecord = await this.getProdutoDados(cleanPai);

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      // No PocketBase, 'field+' anexa sem substituir os arquivos já presentes
      formData.append("arquivos_imagens+", files[i]);
    }

    if (currentRecord) {
      return await pb.collection("produto_dados").update(currentRecord.id, formData, {
        expand: "categoria,tipo_joia"
      });
    } else {
      formData.append("sku_pai", cleanPai);
      formData.append("estado_modelagem", "sem_modelo");
      return await pb.collection("produto_dados").create(formData, {
        expand: "categoria,tipo_joia"
      });
    }
  },

  /**
   * Remove um arquivo de imagem específico de produto_dados.
   * Passa a lista filtrada de arquivos remanescentes para o PocketBase.
   */
  async deleteImage(skuPai, fileName) {
    if (!skuPai || !fileName) return null;
    const cleanPai = getBaseSku(cleanSku(skuPai));
    const record = await this.getProdutoDados(cleanPai);
    if (!record) return null;

    const currentFiles = getArquivosArray(record.arquivos_imagens);
    const updatedFiles = currentFiles.filter(f => (typeof f === "string" ? f : f.name) !== fileName);

    return await pb.collection("produto_dados").update(record.id, {
      arquivos_imagens: updatedFiles
    }, { expand: "categoria,tipo_joia" });
  },

  /**
   * Anexa um ou múltiplos arquivos 3D/modelagem (.stl, .3dm, etc.) a produto_dados.
   * Utiliza 'arquivos_modelagem+' para anexar sem sobrescrever.
   */
  async uploadModelagemFiles(skuPai, files) {
    if (!skuPai || !files || files.length === 0) return null;
    const cleanPai = getBaseSku(cleanSku(skuPai));

    const currentRecord = await this.getProdutoDados(cleanPai);

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append("arquivos_modelagem+", files[i]);
    }

    if (currentRecord) {
      return await pb.collection("produto_dados").update(currentRecord.id, formData, {
        expand: "categoria,tipo_joia"
      });
    } else {
      formData.append("sku_pai", cleanPai);
      formData.append("estado_modelagem", "em_modelagem");
      return await pb.collection("produto_dados").create(formData, {
        expand: "categoria,tipo_joia"
      });
    }
  },

  /**
   * Remove um arquivo 3D específico de produto_dados.
   */
  async deleteModelagemFile(skuPai, fileName) {
    if (!skuPai || !fileName) return null;
    const cleanPai = getBaseSku(cleanSku(skuPai));
    const record = await this.getProdutoDados(cleanPai);
    if (!record) return null;

    const currentFiles = getArquivosArray(record.arquivos_modelagem);
    const updatedFiles = currentFiles.filter(f => (typeof f === "string" ? f : f.name) !== fileName);

    return await pb.collection("produto_dados").update(record.id, {
      arquivos_modelagem: updatedFiles
    }, { expand: "categoria,tipo_joia" });
  },

  /**
   * Atualiza se o SKU Pai possui molde de borracha (fluxo sem impressão 3D).
   * Suporta persistência híbrida e resiliente (coluna tem_molde + JSON ficha_tecnica),
   * sincroniza com catalog e dispara evento global para atualização imediata em memória.
   */
  async updateTemMolde(skuPai, temMolde) {
    if (!skuPai) return null;
    const cleanPai = getBaseSku(cleanSku(skuPai));
    const isMolde = !!temMolde;

    const current = await this.getProdutoDados(cleanPai);
    const currentFicha = current?.ficha_tecnica && typeof current.ficha_tecnica === "object"
      ? current.ficha_tecnica
      : {};

    const updatedFicha = {
      ...currentFicha,
      tem_molde: isMolde,
      tipo_processo: isMolde ? "borracha" : "impressao_3d"
    };

    let updatedRecord = null;

    // 1. Tenta salvar na raiz (tem_molde) e dentro da ficha_tecnica
    try {
      updatedRecord = await this.upsertProdutoDados(cleanPai, {
        tem_molde: isMolde,
        ficha_tecnica: updatedFicha
      });
    } catch (err) {
      console.warn("[produtoDadosService] Falha ao gravar tem_molde na raiz, tentando via ficha_tecnica:", err?.message);
      // Fallback seguro se o schema do PB não tiver a coluna tem_molde
      updatedRecord = await this.upsertProdutoDados(cleanPai, {
        ficha_tecnica: updatedFicha
      });
    }

    // 2. Sincroniza registro no catálogo (se existir)
    try {
      const catalogRecs = await pb.collection("catalog").getFullList({
        filter: `sku = "${cleanPai}" || sku ~ "${cleanPai}-"`
      });
      for (const catRec of catalogRecs) {
        await pb.collection("catalog").update(catRec.id, {
          full_data: {
            ...(catRec.full_data || {}),
            tem_molde: isMolde,
            processo_fabricacao: isMolde ? "borracha" : "impressao_3d"
          }
        }).catch(() => { });
      }
    } catch (catErr) {
      console.warn("[produtoDadosService] Aviso ao sincronizar tem_molde no catalog:", catErr);
    }

    // 3. Notifica a aplicação em tempo real
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("produto-dados-molde-updated", {
        detail: { skuPai: cleanPai, temMolde: isMolde }
      }));
    }

    return updatedRecord;
  },

  /**
   * Busca mapa de todos os SKUs Pai configurados com molde de borracha.
   */
  async getMoldeMap() {
    try {
      const records = await pb.collection("produto_dados").getFullList({
        fields: "id,sku_pai,tem_molde,ficha_tecnica"
      });
      const map = {};
      records.forEach(r => {
        const cleanPai = getBaseSku(cleanSku(r.sku_pai));
        if (cleanPai && checkHasMolde(r)) {
          map[cleanPai] = true;
        }
      });
      return map;
    } catch (e) {
      console.warn("[produtoDadosService] Erro ao carregar molde map:", e);
      return {};
    }
  },

  /**
   * Retorna todos os SKUs Pai que possuem molde de borracha ativo,
   * acompanhados dos dados completos do subestoque de cera.
   */
  async getAllMoldesWithEstoque() {
    try {
      const records = await pb.collection("produto_dados").getFullList({
        sort: "sku_pai",
        fields: "id,sku_pai,tem_molde,ficha_tecnica,arquivos_imagens,categoria,tipo_joia,updated"
      });

      const moldes = [];
      records.forEach(r => {
        const cleanPai = getBaseSku(cleanSku(r.sku_pai));
        if (cleanPai && checkHasMolde(r)) {
          moldes.push({
            id: r.id,
            skuPai: cleanPai,
            temMolde: true,
            subestoque: extractSubestoqueCera(r),
            config_borracha: extractConfigBorracha(r),
            ficha_tecnica: r.ficha_tecnica || {},
            arquivos_imagens: r.arquivos_imagens,
            categoria: r.categoria,
            tipo_joia: r.tipo_joia,
            updated: r.updated
          });
        }
      });
      return moldes;
    } catch (e) {
      console.warn("[produtoDadosService] Erro ao listar moldes com estoque:", e);
      return [];
    }
  },

  /**
   * Atualiza o subestoque de cera (Cera Bruta e Cera Pré-Cravada) de forma atômica e auditada.
   * Suporta deltas (+5, -1), valores absolutos, alteração de gaveta, detalhamento por aro e histórico de movimentações.
   */
  async updateSubestoqueCera(skuPai, params = {}) {
    if (!skuPai) return null;
    const cleanPai = getBaseSku(cleanSku(skuPai));

    const currentRecord = await this.getProdutoDados(cleanPai);
    const currentFicha = currentRecord?.ficha_tecnica && typeof currentRecord.ficha_tecnica === "object"
      ? currentRecord.ficha_tecnica
      : {};

    const currentEstoque = extractSubestoqueCera(currentRecord);

    let nextBruta = currentEstoque.cera_bruta;
    let nextPrecravada = currentEstoque.cera_precravada;
    let nextPorAro = { ...(currentEstoque.estoque_por_aro || {}) };

    // 1. Caso 1: Fornecida matriz completa de estoque por aro (ex: Inventário de Anel com grade de aros)
    if (params.estoquePorAro && typeof params.estoquePorAro === "object") {
      nextPorAro = {};
      let sumBruta = 0;
      let sumPrecravada = 0;
      Object.entries(params.estoquePorAro).forEach(([aroKey, val]) => {
        const cleanAro = String(aroKey).trim();
        if (!cleanAro) return;
        const b = Math.max(0, parseInt(val.bruta ?? val.cera_bruta, 10) || 0);
        const p = Math.max(0, parseInt(val.precravada ?? val.cera_precravada, 10) || 0);
        nextPorAro[cleanAro] = { bruta: b, precravada: p };
        sumBruta += b;
        sumPrecravada += p;
      });
      nextBruta = sumBruta;
      nextPrecravada = sumPrecravada;
    } else if (params.deltaAro && params.deltaAro.aro) {
      // 2. Caso 2: Delta pontual em um aro específico (ex: Enviar 2 ceras do Aro 16 para fundição)
      const aroKey = String(params.deltaAro.aro).trim();
      const currentAro = nextPorAro[aroKey] || { bruta: 0, precravada: 0 };
      const dBruta = parseInt(params.deltaAro.deltaBruta, 10) || 0;
      const dPrecravada = parseInt(params.deltaAro.deltaPrecravada, 10) || 0;
      const newAroBruta = Math.max(0, currentAro.bruta + dBruta);
      const newAroPrecravada = Math.max(0, currentAro.precravada + dPrecravada);
      nextPorAro[aroKey] = { bruta: newAroBruta, precravada: newAroPrecravada };

      // Se houver mapa de aros, recalcula os totais globais como a soma de todos os aros
      const aroKeys = Object.keys(nextPorAro);
      if (aroKeys.length > 0) {
        nextBruta = aroKeys.reduce((acc, k) => acc + (nextPorAro[k].bruta || 0), 0);
        nextPrecravada = aroKeys.reduce((acc, k) => acc + (nextPorAro[k].precravada || 0), 0);
      } else {
        nextBruta = Math.max(0, nextBruta + dBruta);
        nextPrecravada = Math.max(0, nextPrecravada + dPrecravada);
      }
    } else {
      // 3. Caso 3: Padrão global sem especificação de aro (pingentes, brincos ou ajuste global)
      if (params.newBruta !== undefined) {
        nextBruta = Math.max(0, parseInt(params.newBruta, 10) || 0);
      } else if (params.deltaBruta !== undefined) {
        nextBruta = Math.max(0, nextBruta + (parseInt(params.deltaBruta, 10) || 0));
      }

      if (params.newPrecravada !== undefined) {
        nextPrecravada = Math.max(0, parseInt(params.newPrecravada, 10) || 0);
      } else if (params.deltaPrecravada !== undefined) {
        nextPrecravada = Math.max(0, nextPrecravada + (parseInt(params.deltaPrecravada, 10) || 0));
      }
    }

    const nextGaveta = params.localizacao_gaveta !== undefined
      ? String(params.localizacao_gaveta).trim()
      : currentEstoque.localizacao_gaveta;

    const nextPedras = params.pedras_padrao !== undefined
      ? String(params.pedras_padrao).trim()
      : currentEstoque.pedras_padrao;

    // Registra nova linha de histórico (limite de 30 para manter o payload leve)
    const historicoItem = {
      data: new Date().toISOString(),
      tipo: params.tipoOperacao || "AJUSTE",
      delta_bruta: nextBruta - currentEstoque.cera_bruta,
      delta_precravada: nextPrecravada - currentEstoque.cera_precravada,
      saldo_bruta: nextBruta,
      saldo_precravada: nextPrecravada,
      aro: params.deltaAro?.aro || params.aro || null,
      motivo: params.motivo || "Atualização de estoque",
      usuario: params.usuario || "Operador"
    };

    const historicoAtual = Array.isArray(currentEstoque.historico) ? currentEstoque.historico : [];
    const novoHistorico = [historicoItem, ...historicoAtual].slice(0, 30);

    const updatedSubestoque = {
      cera_bruta: nextBruta,
      cera_precravada: nextPrecravada,
      localizacao_gaveta: nextGaveta,
      pedras_padrao: nextPedras,
      estoque_por_aro: nextPorAro,
      ultima_atualizacao: new Date().toISOString(),
      atualizado_por: params.usuario || "Operador",
      historico: novoHistorico
    };

    const updatedFicha = {
      ...currentFicha,
      subestoque_cera: updatedSubestoque
    };

    const updatedRecord = await this.upsertProdutoDados(cleanPai, {
      ficha_tecnica: updatedFicha
    });

    // Notifica a aplicação em tempo real
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("produto-dados-cera-updated", {
        detail: {
          skuPai: cleanPai,
          subestoque: updatedSubestoque
        }
      }));
    }

    return {
      success: true,
      subestoque: updatedSubestoque,
      record: updatedRecord
    };
  },

  /**
   * Baixa atômica de cera ao atender e avançar um pedido para Fundição.
   */
  async consumeCeraForOrder(skuPai, { tipoCera = "bruta", aro = null, orderNumber = "AVULSO", usuario = "Operador" } = {}) {
    const isPrecravada = tipoCera === "precravada";
    const deltaParams = aro ? {
      deltaAro: {
        aro: String(aro).trim(),
        deltaBruta: isPrecravada ? 0 : -1,
        deltaPrecravada: isPrecravada ? -1 : 0
      }
    } : {
      deltaBruta: isPrecravada ? 0 : -1,
      deltaPrecravada: isPrecravada ? -1 : 0
    };

    return await this.updateSubestoqueCera(skuPai, {
      ...deltaParams,
      tipoOperacao: "SAIDA_PEDIDO",
      motivo: `Baixa automática p/ Pedido #${orderNumber}${aro ? ` (Aro ${aro})` : ""}`,
      usuario
    });
  },

  /**
   * Atualiza as configurações técnicas da borracha (código de 6 dígitos da máquina,
   * temperaturas de tanque e bico, tempo de vácuo, pressões, tempos, peso da cera,
   * pedras padrão e observações) no JSON ficha_tecnica.config_borracha.
   */
  async updateConfigBorracha(skuPai, configObj = {}, usuario = "Operador") {
    if (!skuPai) return null;
    const cleanPai = getBaseSku(cleanSku(skuPai));

    const currentRecord = await this.getProdutoDados(cleanPai);
    const currentFicha = currentRecord?.ficha_tecnica && typeof currentRecord.ficha_tecnica === "object"
      ? currentRecord.ficha_tecnica
      : {};

    const existingConfig = extractConfigBorracha(currentRecord);
    const updatedConfig = {
      ...existingConfig,
      ...configObj,
      atualizado_em: new Date().toISOString(),
      atualizado_por: usuario
    };

    // Monta string resumida das pedras para o subestoque
    const currentSubestoque = extractSubestoqueCera(currentRecord);
    let pedrasStr = "";
    if (Array.isArray(updatedConfig.pedras_padrao)) {
      pedrasStr = updatedConfig.pedras_padrao.map(p => {
        const q = p.qtd || 1;
        const t = p.tipo || "Zircônia";
        const tam = p.tamanho ? ` ${p.tamanho}` : "";
        const c = p.cor ? ` (${p.cor})` : "";
        return `${q}x ${t}${tam}${c}`.trim();
      }).filter(Boolean).join(" + ");
    } else if (typeof updatedConfig.pedras_padrao === "string") {
      pedrasStr = updatedConfig.pedras_padrao;
    }

    const updatedSubestoque = {
      ...currentSubestoque,
      pedras_padrao: pedrasStr || currentSubestoque.pedras_padrao
    };

    const updatedFicha = {
      ...currentFicha,
      config_borracha: updatedConfig,
      subestoque_cera: updatedSubestoque
    };

    const updatedRecord = await this.upsertProdutoDados(cleanPai, {
      ficha_tecnica: updatedFicha
    });

    // Dispara eventos em tempo real para sincronização de abas e componentes
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("produto-dados-config-borracha-updated", {
        detail: {
          skuPai: cleanPai,
          configBorracha: updatedConfig
        }
      }));
      window.dispatchEvent(new CustomEvent("produto-dados-cera-updated", {
        detail: {
          skuPai: cleanPai,
          subestoque: updatedSubestoque
        }
      }));
    }

    return {
      success: true,
      configBorracha: updatedConfig,
      record: updatedRecord
    };
  }
};

/**
 * Helper universal para verificar se um registro de produto_dados ou item de catálogo tem molde de borracha.
 */
export const checkHasMolde = (pdRecord, catalogItem) => {
  if (pdRecord) {
    if (typeof pdRecord.tem_molde === "boolean") return pdRecord.tem_molde;
    if (typeof pdRecord.ficha_tecnica?.tem_molde === "boolean") return pdRecord.ficha_tecnica.tem_molde;
    if (pdRecord.ficha_tecnica?.tipo_processo === "borracha") return true;
  }
  if (catalogItem) {
    if (typeof catalogItem.tem_molde === "boolean") return catalogItem.tem_molde;
    if (typeof catalogItem.full_data?.tem_molde === "boolean") return catalogItem.full_data.tem_molde;
    if (catalogItem.full_data?.processo_fabricacao === "borracha") return true;
  }
  return false;
};

/**
 * Helper para extrair o objeto de subestoque de cera normalizado de um produto.
 */
export const extractSubestoqueCera = (pdRecord, catalogItem) => {
  const data = pdRecord?.ficha_tecnica?.subestoque_cera || catalogItem?.full_data?.subestoque_cera || {};

  // Normaliza o mapa estoque_por_aro: { [aro]: { bruta: number, precravada: number } }
  const rawPorAro = data.estoque_por_aro || {};
  const normalizedPorAro = {};
  if (typeof rawPorAro === "object" && rawPorAro !== null) {
    Object.entries(rawPorAro).forEach(([aroKey, val]) => {
      const cleanAro = String(aroKey).trim();
      if (cleanAro && typeof val === "object" && val !== null) {
        normalizedPorAro[cleanAro] = {
          bruta: Math.max(0, parseInt(val.bruta ?? val.cera_bruta, 10) || 0),
          precravada: Math.max(0, parseInt(val.precravada ?? val.cera_precravada, 10) || 0)
        };
      }
    });
  }

  return {
    cera_bruta: Math.max(0, parseInt(data.cera_bruta, 10) || 0),
    cera_precravada: Math.max(0, parseInt(data.cera_precravada, 10) || 0),
    localizacao_gaveta: data.localizacao_gaveta || "",
    pedras_padrao: data.pedras_padrao || "",
    ultima_atualizacao: data.ultima_atualizacao || null,
    atualizado_por: data.atualizado_por || "",
    historico: Array.isArray(data.historico) ? data.historico : [],
    estoque_por_aro: normalizedPorAro
  };
};

/**
 * Helper para extrair a configuração técnica da borracha de um produto.
 * Inclui o código de 6 dígitos da máquina (P1, P2, Tempo, Clamp, Hold, Forward),
 * temperaturas (tanque e bico), tempo de vácuo, peso da cera, qtd de peças, pedras padrão e observações.
 */
export const extractConfigBorracha = (pdRecord, catalogItem) => {
  const data = pdRecord?.ficha_tecnica?.config_borracha || catalogItem?.full_data?.config_borracha || {};

  let pedrasList = [];
  if (Array.isArray(data.pedras_padrao)) {
    pedrasList = data.pedras_padrao;
  } else if (typeof data.pedras_padrao === "string" && data.pedras_padrao.trim()) {
    pedrasList = [{ qtd: 1, tipo: data.pedras_padrao.trim(), tamanho: "", cor: "Branca" }];
  } else if (pdRecord?.ficha_tecnica?.subestoque_cera?.pedras_padrao) {
    pedrasList = [{ qtd: 1, tipo: pdRecord.ficha_tecnica.subestoque_cera.pedras_padrao.trim(), tamanho: "", cor: "Branca" }];
  }

  const pedrasResumo = pedrasList.map(p => {
    const q = p.qtd || 1;
    const t = p.tipo || "Zircônia";
    const tam = p.tamanho ? ` ${p.tamanho}` : "";
    const c = p.cor ? ` (${p.cor})` : "";
    return `${q}x ${t}${tam}${c}`.trim();
  }).filter(Boolean).join(" + ");

  return {
    codigo_maquina: String(data.codigo_maquina || "").trim(), // ex: "1223-1" ou "233412" (onde '-' ou '0' é zero)
    temperatura_tanque: data.temperatura_tanque !== undefined ? String(data.temperatura_tanque) : "", // ex: "69°C"
    temperatura_bico: data.temperatura_bico !== undefined ? String(data.temperatura_bico) : "",     // ex: "71°C"
    tempo_vacuo: data.tempo_vacuo !== undefined ? String(data.tempo_vacuo) : "",                   // ex: "3s"
    pressao_1: data.pressao_1 !== undefined ? String(data.pressao_1) : "",                         // Pressão inicial
    pressao_2: data.pressao_2 !== undefined ? String(data.pressao_2) : "",                         // Pressão final
    tempo_injecao: data.tempo_injecao !== undefined ? String(data.tempo_injecao) : "",             // Tempo injeção
    clamp: data.clamp !== undefined ? String(data.clamp) : "",                                     // Pressão clamp
    hold: data.hold !== undefined ? String(data.hold) : "",                                         // Tempo hold
    forward: data.forward !== undefined ? String(data.forward) : "",                               // Pressão forward
    peso_cera_g: data.peso_cera_g !== undefined ? String(data.peso_cera_g) : "",                   // ex: "0.85"
    qtd_moldes: data.qtd_moldes !== undefined ? String(data.qtd_moldes) : "1",                     // ex: "1"
    tipo_modelo: data.tipo_modelo || "",                                                           // ex: "Pingente", "Anel"
    aros_disponiveis: Array.isArray(data.aros_disponiveis) ? data.aros_disponiveis.map(Number).filter(n => !isNaN(n)) : [],
    tolerancia_ajuste_bancada: typeof data.tolerancia_ajuste_bancada === "number" ? data.tolerancia_ajuste_bancada : 1,
    pedras_padrao: pedrasList,
    pedras_resumo: pedrasResumo,
    observacoes: data.observacoes || "",
    atualizado_em: data.atualizado_em || null,
    atualizado_por: data.atualizado_por || ""
  };
};


